import type { UseValidateConfigDependenciesResult } from '@gonfalon/flags/useValidateConfigDependencies';
import type { List } from '@gonfalon/immutable-shim/immutableShim';
import { isImmutable, Map, Record } from '@gonfalon/immutable-shim/immutableShim';
import { msg } from '@gonfalon/messages/msg';
import { isEqual, isNil } from 'lodash';

import type { VariationType } from '#utils/flags/flagConstants';
import type { Flag } from '#utils/flags/records/Flag';
import type { FlagConfiguration } from '#utils/flags/records/FlagConfiguration';
import type { Rule } from '#utils/flags/records/Rule';
import type { Variation } from '#utils/flags/records/Variation';
import { hasHomogeneousVariationTypes } from '#utils/flags/variationTypes';

import type { ClauseValidation } from './clauses';
import { validateClause } from './clauses';
import { minLength, unavailable } from './errors';
import type { ThresholdsType } from './targeting';
import {
  FallthroughValidation,
  GuardedReleasePhaseConfigValidation,
  PrerequisiteValidation,
  RolloutValidation,
  UserTargetsValidation,
  validateFallthrough,
  validateGuardedReleasePhaseConfig,
  validatePrerequisite,
  validateRollout,
  validateUserTargets,
  validateVariation,
  variationDuplicateNames,
  variationDuplicateValues,
  variationNotFound,
  variationOrRollout,
  variationTypeMismatch,
} from './targeting';

import type { FormVariationBase } from '#ia-poc/services/flag-creation/types';
import { FlagCreationErrorMessages } from '#ia-poc/services/flag-creation/utils';

type TargetingRuleValidationProps = {
  clauses: Map<string, ClauseValidation>;
  rollout: RolloutValidation;
  measuredRolloutConfig: GuardedReleasePhaseConfigValidation;
  // oxlint-disable-next-line typescript/no-explicit-any
  root: Map<any, any>;
  duplicateDescription: string;
};

export class TargetingRuleValidation extends Record<TargetingRuleValidationProps>({
  clauses: Map(),
  rollout: new RolloutValidation(),
  measuredRolloutConfig: new GuardedReleasePhaseConfigValidation(),
  root: Map(),
  duplicateDescription: '',
}) {
  isEmpty() {
    return (
      this.measuredRolloutConfig.isEmpty() &&
      this.rollout.isEmpty() &&
      this.root.isEmpty() &&
      this.clauses.isEmpty() &&
      !this.duplicateDescription
    );
  }

  isInvalid() {
    return (
      (!this.clauses?.isEmpty() && this.getIn(['clauses', 'items'])?.some((c: ClauseValidation) => c.isInvalid())) ||
      this.measuredRolloutConfig.isInvalid() ||
      this.rollout.isInvalid() ||
      this.duplicateDescription ||
      this.getIn(['root', 'type']) === 'unavailable' ||
      this.getIn(['root', 'type']) === 'variationNotFound'
    );
  }

  isIncomplete() {
    return (
      (!this.clauses?.isEmpty() && this.getIn(['clauses', 'items'])?.some((c: ClauseValidation) => c.isIncomplete())) ||
      this.getIn(['root', 'type']) === 'variationOrRollout' ||
      this.measuredRolloutConfig.isIncomplete() ||
      this.rollout.isIncomplete()
    );
  }

  getMessage() {
    if (this.isEmpty()) {
      return;
    }

    if (this.duplicateDescription) {
      return this.duplicateDescription;
    }

    if (this.isInvalid()) {
      if (this.getIn(['root', 'type']) === 'unavailable') {
        return msg('targeting.ruleValidation.segmentUnavailable');
      }

      if (this.getIn(['root', 'type']) === 'variationNotFound') {
        return msg('targeting.ruleValidation.variationNotFound');
      }
    }

    if (this.isIncomplete()) {
      if (this.getIn(['root', 'type']) === 'variationOrRollout') {
        return msg('targeting.ruleValidation.variationOrRolloutRequired');
      }

      if (this.measuredRolloutConfig.isIncomplete()) {
        return this.measuredRolloutConfig.getMessage();
      }
    }
  }
}

type FlagConfigurationValidationProps = {
  fallthrough: FallthroughValidation;
  userTargets: UserTargetsValidation;
  prerequisites: Map<string, PrerequisiteValidation>;
  rules: Map<string, TargetingRuleValidation>;
};

export class FlagConfigurationValidation extends Record<FlagConfigurationValidationProps>({
  fallthrough: new FallthroughValidation(),
  userTargets: new UserTargetsValidation(),
  prerequisites: Map(),
  rules: Map(),
}) {
  isEmpty() {
    return this.fallthrough.isEmpty() && this.prerequisites.isEmpty() && this.rules.isEmpty();
  }

  isInvalid() {
    return (
      this.fallthrough.isInvalid() ||
      this.userTargets.isAlert() ||
      this.prerequisites.some((r) => r.isInvalid()) ||
      this.rules.some((r) => r.isInvalid())
    );
  }

  isIncomplete() {
    return (
      this.fallthrough.isIncomplete() ||
      this.prerequisites.some((r) => r.isIncomplete()) ||
      this.rules.some((r) => r.isIncomplete())
    );
  }

  isValid() {
    return !this.isInvalid() && !this.isIncomplete();
  }
}

export function validateFlagConfiguration(
  config: FlagConfiguration,
  variations: List<Variation>,
  { userTargetAlertThreshold, userTargetWarningThreshold }: ThresholdsType = {},
) {
  let validation = new FlagConfigurationValidation();

  for (const prereq of config.prerequisites) {
    const result = validatePrerequisite(prereq);
    if (!result.isEmpty()) {
      validation = validation.setIn(['prerequisites', prereq._key], result);
    }
  }

  for (const rule of config.rules) {
    const result = validateRule(rule, config.rules);
    if (!result.isEmpty()) {
      validation = validation.setIn(['rules', rule._key], result);
    }
  }

  const userTargetsResult = validateUserTargets(config.targets, {
    userTargetAlertThreshold,
    userTargetWarningThreshold,
  });
  if (!userTargetsResult.isEmpty()) {
    validation = validation.set('userTargets', userTargetsResult);
  }

  const fallthroughResult = validateFallthrough(config.fallthrough, variations);
  if (!fallthroughResult.isEmpty()) {
    validation = validation.set('fallthrough', fallthroughResult);
  }

  return validation;
}

export function validateFlagConfigurationWithDependencies(
  config: FlagConfiguration,
  dependenciesResult: UseValidateConfigDependenciesResult,
  baseValidation: FlagConfigurationValidation = new FlagConfigurationValidation(),
): FlagConfigurationValidation {
  let validation = baseValidation;
  const { invalid } = dependenciesResult;

  // Mark prerequisites referencing missing flag keys.
  // Skip empty keys — those are new prerequisites without a flag selected yet and should
  // show "You must select a feature flag" (isIncomplete) rather than "flag no longer exists".
  for (const invalidPrereq of invalid.prerequisites) {
    const prereq = config.prerequisites.find((p) => !!(p.key && p.key === invalidPrereq.key));
    if (prereq?._key) {
      const existing = validation.prerequisites.get(prereq._key) ?? new PrerequisiteValidation();
      validation = validation.setIn(
        ['prerequisites', prereq._key],
        existing.set('key', unavailable(invalidPrereq.key)),
      );
    }
  }

  // Mark prerequisites with invalid variation references (same empty-key guard as above)
  for (const invalidPrereq of invalid.prerequisiteVariations) {
    const prereq = config.prerequisites.find((p) => !!(p.key && p.key === invalidPrereq.key));
    if (prereq?._key) {
      const existing = validation.prerequisites.get(prereq._key) ?? new PrerequisiteValidation();
      validation = validation.setIn(
        ['prerequisites', prereq._key],
        existing.set('key', variationNotFound(prereq.variation)),
      );
    }
  }

  // Mark rules that reference missing segments
  for (const invalidSegment of invalid.segments) {
    for (const rule of config.rules) {
      const hasSegmentClause = rule.clauses.some(
        (c) => c.op === 'segmentMatch' && c.values.includes(invalidSegment.key),
      );
      if (hasSegmentClause && rule._key) {
        const existing = validation.rules.get(rule._key) ?? new TargetingRuleValidation();
        validation = validation.setIn(['rules', rule._key], existing.set('root', unavailable(invalidSegment.key)));
      }
    }
  }

  // Mark fallthrough and rules with out-of-range variation indices
  const invalidVariationIndices = new Set(invalid.variations.map((v) => v.variationIndex));

  if (typeof config.fallthrough.variation === 'number' && invalidVariationIndices.has(config.fallthrough.variation)) {
    validation = validation.set(
      'fallthrough',
      validation.fallthrough.set('root', variationNotFound(config.fallthrough.variation)),
    );
  }

  for (const rule of config.rules) {
    if (typeof rule.variation === 'number' && invalidVariationIndices.has(rule.variation) && rule._key) {
      const existing = validation.rules.get(rule._key) ?? new TargetingRuleValidation();
      if (existing.getIn(['root', 'type']) === 'unavailable') {
        continue;
      }
      validation = validation.setIn(['rules', rule._key], existing.set('root', variationNotFound(rule.variation)));
    }
  }

  return validation;
}

export const validateFlagVariations = (flag: Flag, variationType: VariationType) => {
  let validation = Map();

  if (variationType) {
    if (flag.getVariationType() !== variationType && flag.getPotentialVariationType() !== variationType) {
      validation = validation.set('root', variationTypeMismatch(flag.variations, variationType));
    }
  }

  if (!hasHomogeneousVariationTypes(flag)) {
    validation = validation.set('root', variationTypeMismatch(flag.variations, undefined));
  }

  if (
    flag.variations.some((variationA, index) =>
      flag.variations
        .slice(index + 1)
        .some((variationB) =>
          isEqual(
            isImmutable(variationA.value) ? variationA.value.toJS() : variationA.value,
            isImmutable(variationB.value) ? variationB.value.toJS() : variationB.value,
          ),
        ),
    )
  ) {
    validation = validation.set('root', variationDuplicateValues(flag.variations, flag.variations));
  }

  //Empty strings for names are fine, but any non-empty string must be unique
  const names = flag.variations.map((v) => v.name).filter((v) => v !== '');
  if (names.toSet().size !== names.size) {
    validation = validation.set('root', variationDuplicateNames(flag.variations, flag.variations));
  }

  for (const variation of flag.variations) {
    const result = validateVariation(variation);
    if (!result.isEmpty()) {
      validation = validation.set(variation._key, result);
    }
  }

  return validation;
};

export function validateRule(rule: Rule, allRules?: List<Rule>) {
  let validation = new TargetingRuleValidation();

  if (rule.clauses.isEmpty()) {
    validation = validation.setIn(['clauses', 'root'], minLength(1, rule.clauses.size));
  }

  if ((isNil(rule.variation) && isNil(rule.rollout)) || (!isNil(rule.variation) && !isNil(rule.rollout))) {
    validation = validation.set('root', variationOrRollout(rule.variation, rule.rollout));
  }

  for (const clause of rule.clauses) {
    const result = validateClause(clause, rule.clauses);
    if (!result.isEmpty()) {
      validation = validation.setIn(['clauses', 'items', clause._key], result);
    }
  }

  if (rule.rollout) {
    const result = validateRollout(rule.rollout, undefined, undefined);
    if (!result.isEmpty()) {
      validation = validation.set('rollout', result);
    }
  }

  if (rule.measuredRolloutConfig) {
    const result = validateGuardedReleasePhaseConfig(rule.measuredRolloutConfig);
    if (!result.isEmpty()) {
      validation = validation.set('measuredRolloutConfig', result);
    }
  }

  if (allRules) {
    const hasDuplicates = allRules.filter(({ description }) => description && description === rule.description);
    if (hasDuplicates.size > 1) {
      validation = validation.set('duplicateDescription', 'duplicate description');
    }
  }

  return validation;
}

export const checkAllVariationNamesUnique = (currentVariations: FormVariationBase[]) => {
  const nameSet = new Set();

  for (const variation of currentVariations) {
    if (!variation.name) {
      continue;
    }

    if (nameSet.has(variation.name)) {
      return FlagCreationErrorMessages.UNIQUE_VARIATION_NAMES;
    }

    nameSet.add(variation.name);
  }

  return true;
};
